import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Breadcrumbs, { BreadcrumbsItem } from '../../../template/MainTheme/components/Breadcrumbs';
import { Society } from '../../../app/models/Society';
import { Client } from '../../../app/models/Client';
import { PaymentCondition } from '../../../app/models/PaymentCondition';
import useQuoteService from '../../../app/services/hooks/useQuoteService';
import { Currency } from '../../../app/models/Currency';
import {
    defaultQuoteDataForm,
    QuoteDataForm,
    QuoteOfferTypeDataForm
} from '../../../app/models/Quote';
import LazyLoading from '../../../components/LazyLoading';
import DefaultCard from '../../../components/default/DefaultCard';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import { toast } from 'react-toastify';
import QuoteDetailsContainer from '../components/QuoteDetailsContainer';
import { AuthContext } from '../../../contexts/AuthContext';
import { Tab, Tabs } from 'react-bootstrap';
import '../../../assets/scss/custom/components/_tab.scss';
import QuoteTab from '../components/tabs/QuoteTab';
import DefaultCreatedModifiedUserForm from '../../../components/default/DefaultCreatedModifiedUserForm';
import QuoteRouteSegmentsContainer from '../components/QuoteRouteSegmentsContainer';
import { QuoteRouteSegment, QuoteRouteSegmentFilters } from '../../../app/models/QuoteRouteSegment';
import useQuoteRouteSegmentService from '../../../app/services/hooks/useQuoteRouteSegmentService';
import { AppContext } from '../../../contexts/AppContext';
import useNavigationPage from '../../../hooks/useNavigationPage';
import { Unit } from '../../../app/models/Unit';
import SelectTwo, { SelectTwoMapperOptions } from '../../../components/SelectTwo';
import ButtonSaveForm from '../../../components/buttons/ButtonSaveForm';
import useHandleErrorFields from '../../../hooks/useHandleErrorFields';

type RouterParams = {
    id: string;
};

const QuoteEdit = () => {
    const { navigationPage } = useNavigationPage();

    const { showLoading, hideLoading } = useContext(AppContext);

    const { id } = useParams<RouterParams>();

    const [verifyingQuote, setVerifyingQuote] = useState<boolean>(true);

    const { editQuote, fetchingEditQuote, updateQuote } = useQuoteService();
    const { getQuoteRouteSegments, fetchingGetQuoteRouteSegments } = useQuoteRouteSegmentService();

    const { auth } = useContext(AuthContext);

    const [quoteForm, setQuoteForm] = useState<QuoteDataForm>({ ...defaultQuoteDataForm });

    const [societies, setSocieties] = useState<Society[]>([]);
    const [clients, setClients] = useState<Client[]>([]);
    const [paymentConditions, setPaymentsConditions] = useState<PaymentCondition[]>([]);
    const [paymentMethods, setPaymentMethods] = useState<PaymentCondition[]>([]);
    const [currencies, setCurrencies] = useState<Currency[]>([]);
    const [offerTypes, setOfferTypes] = useState<QuoteOfferTypeDataForm[]>([]);
    const [units, setUnits] = useState<Unit[]>([]);

    const [quoteRouteSegments, setQuoteRouteSegments] = useState<QuoteRouteSegment[]>([]);
    const [quoteCheckDocument, setQuoteCheckDocument] = useState<boolean>(false);
    const [disableUpdate, setDisableUpdate] = useState<boolean>(false);
    const [filter, setFilter] = useState<QuoteRouteSegmentFilters>({
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'asc'
    });

    useEffect(() => {
        edit(parseInt(id));
    }, [id]);

    useEffect(() => {
        reloadQuoteRouteSegmentTable();
    }, [filter]);

    const [errorFields, setErrorFields] = useState<Record<string, unknown> | undefined>();
    const { fieldErrorMessage, fieldHasError, onFocusRemove, setErrors } = useHandleErrorFields();

    useEffect(() => {
        setErrors(errorFields);
    }, [errorFields, setErrors]);

    const edit = (id: number) => {
        editQuote(id, {
            onSuccess: (response: ServiceResponse) => {
                setSocieties(response.data.societies);
                setClients(response.data.clients);
                setPaymentMethods(response.data.payment_methods);
                setCurrencies(response.data.currencies);
                setOfferTypes(response.data.offer_types);
                setPaymentsConditions(response.data.payment_conditions);
                setUnits(response.data.units);

                setQuoteForm(response.data.quote);
                setQuoteCheckDocument(response.data.quote.is_route_segment_document);

                if (!response.data.can_update) {
                    setDisableUpdate(true);
                }
                setVerifyingQuote(false);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message, {
                    autoClose: 2500
                });
                if (response.data.to_dashboard) {
                    navigationPage('/quotes');
                }
            }
        });
    };

    const update = () => {
        if (showLoading) showLoading('loading', 'Actualizando Oferta...');

        if (!quoteForm.id) {
            return;
        }

        const quoteId = quoteForm.id;

        // Si el switch de peso padrón está desactivado, enviar null en los campos relacionados
        const dataToUpdate = { ...quoteForm };
        if (!quoteForm.use_standard_weight) {
            dataToUpdate.standard_weight_value = null;
            dataToUpdate.standard_weight_unit_id = null;
            dataToUpdate.standard_weight_lower_limit = null;
            dataToUpdate.standard_weight_lower_limit_unit_id = null;
        }

        updateQuote(quoteId, dataToUpdate, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                toast.success(response.message);
                setErrorFields(undefined);
                setQuoteForm((prev) => ({ ...prev, ...response.data.quote }));
                edit(quoteId);
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                toast.error(response.message);
            },
            onFieldError: (errorFields: ServiceResponse) => {
                if (hideLoading) hideLoading();

                setErrorFields(errorFields.data);
            }
        });
    };

    const reloadQuoteRouteSegmentTable = () => {
        getQuoteRouteSegments(parseInt(id), filter, {
            onSuccess: (response: ServiceResponse) => {
                setQuoteRouteSegments(response.data.quote_route_segments);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message, {
                    autoClose: 2500
                });
            }
        });
    };

    const breadcrumbs: BreadcrumbsItem[] = [
        {
            name: 'Oferta',
            url: '/quotes',
            isActive: false
        },
        {
            name: 'Editar Oferta #' + id,
            url: '/quotes/' + id + '/edit',
            isActive: true
        }
    ];

    if (verifyingQuote) {
        return (
            <DefaultCard>
                <LazyLoading height={300} />
            </DefaultCard>
        );
    }

    const hasStandardWeight = Boolean(quoteForm.use_standard_weight);

    const handleStandardWeightToggle = (checked: boolean) => {
        if (checked) {
            setQuoteForm((prev) => ({
                ...prev,
                use_standard_weight: true
            }));
            return;
        }

        // Limpiar errores de los campos de peso padrón
        onFocusRemove('standard_weight_value');
        onFocusRemove('standard_weight_unit_id');
        onFocusRemove('standard_weight_lower_limit');
        onFocusRemove('standard_weight_lower_limit_unit_id');

        // Limpiar también los errores del estado general
        if (errorFields) {
            const newErrorFields = { ...errorFields };
            delete newErrorFields['standard_weight_value'];
            delete newErrorFields['standard_weight_unit_id'];
            delete newErrorFields['standard_weight_lower_limit'];
            delete newErrorFields['standard_weight_lower_limit_unit_id'];
            setErrorFields(Object.keys(newErrorFields).length > 0 ? newErrorFields : undefined);
        }

        setQuoteForm((prev) => ({
            ...prev,
            use_standard_weight: false,
            standard_weight_value: null,
            standard_weight_unit_id: null,
            standard_weight_lower_limit: null,
            standard_weight_lower_limit_unit_id: null
        }));
    };

    return (
        <>
            <Breadcrumbs pageSection={`Editar Oferta #${id}`} breadcrumbs={breadcrumbs} />

            {/* Tabs */}

            <Tabs defaultActiveKey="general-data" id="quote-tabs" className="mb-3">
                <Tab eventKey="general-data" title="Datos Generales">
                    <QuoteTab
                        auth={auth}
                        fetchingQuote={fetchingEditQuote}
                        update={update}
                        quoteForm={quoteForm}
                        setQuoteForm={setQuoteForm}
                        societies={societies}
                        clients={clients}
                        paymentConditions={paymentConditions}
                        paymentMethods={paymentMethods}
                        offerTypes={offerTypes}
                        currencies={currencies}
                        units={units}
                        errorFields={errorFields}
                        disableUpdate={disableUpdate}
                    />
                </Tab>

                <Tab eventKey="interlocutors" title="Interlocutores" disabled>
                    Interlocutores
                </Tab>

                <Tab eventKey="billing" title="Facturación">
                    <DefaultCard>
                        <div className="h3 mt-0 mb-2 card-title">Facturación</div>

                        <div className="mb-2 row">
                            <label className="col-md-3 col-form-label">Forma de Pago</label>
                            <div className="col-md-9">
                                <SelectTwo
                                    name="payment_method_id"
                                    id="payment_method_id"
                                    inputValue={quoteForm.payment_method_id}
                                    options={SelectTwoMapperOptions(paymentMethods)}
                                    onChange={(option) =>
                                        setQuoteForm((prev) => ({
                                            ...prev,
                                            payment_method_id:
                                                (option as { value?: number | string | null } | null)?.value ?? null
                                        }))
                                    }
                                    hasError={fieldHasError('payment_method_id') !== ''}
                                    onFocus={() => onFocusRemove('payment_method_id')}
                                    placeholder={'Seleccione una forma de pago'}
                                />
                                <div className="invalid-feedback" style={{ display: 'flex' }}>
                                    {fieldErrorMessage('payment_method_id')}
                                </div>
                            </div>
                        </div>

                        <div className="mb-2 row">
                            <label className="col-md-3 col-form-label">Condición de Pago</label>
                            <div className="col-md-9">
                                <div className="row g-1">
                                    <div className="col-9">
                                        <SelectTwo
                                            name="payment_condition_id"
                                            id="payment_condition_id"
                                            inputValue={quoteForm.payment_condition_id}
                                            options={SelectTwoMapperOptions(paymentConditions)}
                                            onChange={(option) =>
                                                setQuoteForm((prev) => ({
                                                    ...prev,
                                                    payment_condition_id:
                                                        (option as { value?: number | string | null } | null)?.value ?? null
                                                }))
                                            }
                                            hasError={fieldHasError('payment_condition_id') !== ''}
                                            onFocus={() => onFocusRemove('payment_condition_id')}
                                            placeholder={'Seleccione una condición de pago'}
                                        />
                                        <div className="invalid-feedback" style={{ display: 'flex' }}>
                                            {fieldErrorMessage('payment_condition_id')}
                                        </div>
                                    </div>
                                    <div className="col-3">
                                        <input
                                            type="number"
                                            className={`form-control ${fieldHasError('days_count')}`}
                                            value={quoteForm.days_count ?? ''}
                                            onChange={(e) =>
                                                    setQuoteForm((prev) => ({
                                                        ...prev,
                                                        days_count: e.target.value
                                                            ? Number(e.target.value)
                                                            : undefined
                                                    }))
                                            }
                                            onFocus={() => onFocusRemove('days_count')}
                                            placeholder="días"
                                        />
                                        <div className="invalid-feedback" style={{ display: 'flex' }}>
                                            {fieldErrorMessage('days_count')}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mb-2 row">
                            <label className="col-md-3 col-form-label">Moneda</label>
                            <div className="col-md-9">
                                <SelectTwo
                                    name="currency_id"
                                    id="currency_id"
                                    inputValue={quoteForm.currency_id}
                                    options={SelectTwoMapperOptions(currencies)}
                                    onChange={(option) =>
                                        setQuoteForm((prev) => ({
                                            ...prev,
                                            currency_id:
                                                (option as { value?: number | string | null } | null)?.value ?? null
                                        }))
                                    }
                                    hasError={fieldHasError('currency_id') !== ''}
                                    onFocus={() => onFocusRemove('currency_id')}
                                    placeholder={'Seleccione una Moneda'}
                                />
                                <div className="invalid-feedback" style={{ display: 'flex' }}>
                                    {fieldErrorMessage('currency_id')}
                                </div>
                            </div>
                        </div>

                        <div className="mb-2 row">
                            <label className="col-md-3 col-form-label">Usar Peso Padrón</label>
                            <div className="col-md-9">
                                <div className="form-check form-switch">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="use_standard_weight"
                                        checked={hasStandardWeight}
                                        onChange={(e) => handleStandardWeightToggle(e.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="use_standard_weight">
                                        {hasStandardWeight ? 'Activado' : 'Desactivado'}
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="mb-2 row">
                            <label className="col-md-3 col-form-label">Peso Padrón</label>
                            <div className="col-md-9">
                                <div className="row g-1">
                                    <div className="col-6">
                                        <input
                                            type="number"
                                            step="0.0001"
                                            className={`form-control ${fieldHasError('standard_weight_value')}`}
                                            value={quoteForm.standard_weight_value ?? ''}
                                            disabled={!hasStandardWeight}
                                            onChange={(e) =>
                                                setQuoteForm((prev) => ({
                                                    ...prev,
                                                    standard_weight_value: e.target.value
                                                        ? Number(e.target.value)
                                                        : null
                                                }))
                                            }
                                            onFocus={() => onFocusRemove('standard_weight_value')}
                                            placeholder="0.0000"
                                        />
                                        <div className="invalid-feedback" style={{ display: 'flex' }}>
                                            {fieldErrorMessage('standard_weight_value')}
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <SelectTwo
                                            name="standard_weight_unit_id"
                                            id="standard_weight_unit_id"
                                            inputValue={quoteForm.standard_weight_unit_id}
                                            options={SelectTwoMapperOptions(units)}
                                            disabled={!hasStandardWeight}
                                            onChange={(option) =>
                                                setQuoteForm((prev) => ({
                                                    ...prev,
                                                    standard_weight_unit_id:
                                                        (option as { value?: number | string | null } | null)?.value ?? null
                                                }))
                                            }
                                            hasError={fieldHasError('standard_weight_unit_id') !== ''}
                                            onFocus={() => onFocusRemove('standard_weight_unit_id')}
                                            placeholder={'Seleccione unidad'}
                                        />
                                        <div className="invalid-feedback" style={{ display: 'flex' }}>
                                            {fieldErrorMessage('standard_weight_unit_id')}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mb-2 row">
                            <label className="col-md-3 col-form-label">Límite Inferior</label>
                            <div className="col-md-9">
                                <div className="row g-1">
                                    <div className="col-6">
                                        <input
                                            type="number"
                                            step="0.0001"
                                            className={`form-control ${fieldHasError('standard_weight_lower_limit')}`}
                                            value={quoteForm.standard_weight_lower_limit ?? ''}
                                            disabled={!hasStandardWeight}
                                            onChange={(e) =>
                                                setQuoteForm((prev) => ({
                                                    ...prev,
                                                    standard_weight_lower_limit: e.target.value
                                                        ? Number(e.target.value)
                                                        : null
                                                }))
                                            }
                                            onFocus={() => onFocusRemove('standard_weight_lower_limit')}
                                            placeholder="0.0000"
                                        />
                                        <div className="invalid-feedback" style={{ display: 'flex' }}>
                                            {fieldErrorMessage('standard_weight_lower_limit')}
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <SelectTwo
                                            name="standard_weight_lower_limit_unit_id"
                                            id="standard_weight_lower_limit_unit_id"
                                            inputValue={quoteForm.standard_weight_lower_limit_unit_id}
                                            options={SelectTwoMapperOptions(units)}
                                            disabled={!hasStandardWeight}
                                            onChange={(option) =>
                                                setQuoteForm((prev) => ({
                                                    ...prev,
                                                    standard_weight_lower_limit_unit_id:
                                                        (option as { value?: number | string | null } | null)?.value ?? null
                                                }))
                                            }
                                            hasError={fieldHasError('standard_weight_lower_limit_unit_id') !== ''}
                                            onFocus={() => onFocusRemove('standard_weight_lower_limit_unit_id')}
                                            placeholder={'Seleccione unidad'}
                                        />
                                        <div className="invalid-feedback" style={{ display: 'flex' }}>
                                            {fieldErrorMessage('standard_weight_lower_limit_unit_id')}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {auth?.roles && (
                            <div className="row justify-content-end">
                                <div className="col-auto">
                                    <ButtonSaveForm callbackSave={update} locked={disableUpdate} />
                                </div>
                            </div>
                        )}
                    </DefaultCard>
                </Tab>
                <Tab eventKey="options" title="Opciones">
                    <div className="col-12">
                        <QuoteRouteSegmentsContainer
                            quoteId={parseInt(id)}
                            quoteCheckDocument={quoteCheckDocument}
                            quoteRouteSegments={quoteRouteSegments}
                            setQuoteRouteSegments={setQuoteRouteSegments}
                            reloadQuoteRouteSegmentTable={reloadQuoteRouteSegmentTable}
                            filter={filter}
                            setFilter={setFilter}
                            fetchingGetQuoteRouteSegments={fetchingGetQuoteRouteSegments}
                        />
                    </div>
                </Tab>
                <Tab eventKey="management" title="Gestión">
                    <DefaultCard>
                        <div className="h3 mt-0 mb-2 card-title">Gestión</div>
                        <DefaultCreatedModifiedUserForm
                            createdBy={quoteForm.created_user}
                            createdAt={quoteForm.created_at}
                            updatedBy={quoteForm.last_modified_user}
                            updatedAt={quoteForm.updated_at}
                            status={quoteForm.status}
                        />
                    </DefaultCard>
                </Tab>
            </Tabs>

            <QuoteDetailsContainer
                quoteId={parseInt(id)}
                currencyId={quoteForm.currency_id ?? ''}
                quoteForm={quoteForm}
                setQuoteForm={setQuoteForm}
                setQuoteRouteSegmentTable={setQuoteRouteSegments}
                reloadQuoteRouteSegmentTable={reloadQuoteRouteSegmentTable}
                disableUpdate={disableUpdate}
                reloadQuote={edit}
            />
        </>
    );
};

export default QuoteEdit;
